import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpHeaders, HttpParams } from '@angular/common/http';
import { finalize } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { SharedService } from 'src/app/services/shared.service';
import { getStoredEmployeeSelection } from 'src/app/services/current-client/current-client.service';

// Hosts that must never receive our bearer token.
//
// blob.core.windows.net matters now that private blobs are handed to the browser as SAS URLs:
// when an Authorization header is present, Azure Storage authenticates with *that* and ignores the
// SAS in the query string. Our token is an Enwage SSO JWT, not an Azure AD storage token, so the
// request is rejected and the file appears inaccessible. Sending it would also leak the token to
// a third-party host.
const SKIP_AUTHORIZATION = [
  'connect/token',
  'https://api.twilio.com/2010-04-01',
  'api.openweathermap.org',
  'blob.core.windows.net',
];

export const headerInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const utilityService = inject(UtilityService);
  const sharedService = inject(SharedService);

  let headersList = new HttpHeaders();
  let addAuthenticationTokenToRequest: boolean = true;
  let isProxyLogin = false;
  let isRefreshGrant = false;

  SKIP_AUTHORIZATION.forEach((x) => {
    if (req.url.toLowerCase().includes('connect/token')) {
      if (req.body) {
        let params = req.body as HttpParams;
        if (params.has('grant_type')) {
          const type = params.get('grant_type');
          if (type == 'refresh_token') {
            addAuthenticationTokenToRequest = true;
            isRefreshGrant = true;
          } else {
            addAuthenticationTokenToRequest = false;
          }
        }
        let headers = req.headers;
        if (headers.has('isProxyRequired')) {
          isProxyLogin = true;
        }
      }
    } else if (req.url.toLocaleLowerCase().includes(x.toLocaleLowerCase())) {
      addAuthenticationTokenToRequest = false;
    }
  });

  const token = authService.GetToken();

  if (addAuthenticationTokenToRequest && !isProxyLogin) {
    headersList = headersList.append('Authorization', `Bearer ${token}`);
    // EN-24306 - SSO rebuilds the claims from the login on every refresh, so the chosen client has to be
    // asked for again each time or the token would fall back to the login's own client row.
    const selectedEmployeeId = getStoredEmployeeSelection();
    if (isRefreshGrant && selectedEmployeeId) {
      headersList = headersList.append('switchEmployeeId', selectedEmployeeId.toString());
    }
    req = req.clone({
      headers: headersList,
    });
  }

  return next(req).pipe(
    finalize(() => {})
  );
};
